import { useEffect, useState } from 'react';

export type FontScale = 'normal' | 'moyen' | 'grand';

const STORAGE_KEY = 'app_font_scale';
const RATIO: Record<FontScale, number> = { normal: 1, moyen: 1.15, grand: 1.3 };

function readStored(): FontScale {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw === 'moyen' || raw === 'grand' ? raw : 'normal';
  } catch {
    return 'normal';
  }
}

// On expose le facteur via une variable CSS `--app-zoom`, appliquée par App.css
// UNIQUEMENT au contenu intérieur de la zone de défilement (.zoomable), pas au
// document entier. Zoomer <html> cassait le calcul de hauteur (.app height:100vh)
// et donc le défilement. Ici le conteneur de scroll garde sa taille réelle ;
// seul son contenu est agrandi (px en dur inclus), sur toutes les pages.
function applyToRoot(scale: FontScale): void {
  if (typeof document === 'undefined') {
    return;
  }
  document.documentElement.style.setProperty('--app-zoom', String(RATIO[scale]));
}

// Applique des le chargement du module (avant le montage React) pour eviter un
// saut de taille au demarrage.
if (typeof document !== 'undefined') {
  applyToRoot(readStored());
}

const EVENT = 'app-font-scale-change';

export function useFontScale(): { scale: FontScale; setScale: (s: FontScale) => void } {
  const [scale, setScaleState] = useState<FontScale>(() => readStored());

  // Synchronise toutes les instances du hook quand une instance change l'echelle.
  useEffect(() => {
    const handler = (e: Event): void => {
      const detail = (e as CustomEvent<FontScale>).detail;
      if (detail === 'normal' || detail === 'moyen' || detail === 'grand') {
        setScaleState((prev) => (prev === detail ? prev : detail));
      }
    };
    window.addEventListener(EVENT, handler);
    return () => window.removeEventListener(EVENT, handler);
  }, []);

  useEffect(() => {
    applyToRoot(scale);
    try {
      localStorage.setItem(STORAGE_KEY, scale);
    } catch {
      /* ignore */
    }
    window.dispatchEvent(new CustomEvent(EVENT, { detail: scale }));
  }, [scale]);

  return { scale, setScale: setScaleState };
}
